import React, { useEffect, useState, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { Heart, Search, Plus, Folder, Trash2, Play, X, Star } from "lucide-react";
import api from "@/lib/api";
import { useFavorites } from "@/context/FavoritesContext";
import { Spinner, EmptyState } from "@/components/Bits";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";

export default function Favorites() {
  const navigate = useNavigate();
  const { reloadFavorites } = useFavorites();
  const [data, setData] = useState(null);
  const [active, setActive] = useState("all");
  const [search, setSearch] = useState("");
  const [sort, setSort] = useState("recent");
  const [newCol, setNewCol] = useState("");
  const [creating, setCreating] = useState(false);

  const load = async () => {
    try { const { data } = await api.get("/favorites"); setData(data); } catch { setData({ collections: [], favorites: [] }); }
  };
  useEffect(() => { load(); }, []);

  const collections = data?.collections || [];
  const favs = data?.favorites || [];

  const visible = useMemo(() => {
    let list = data?.favorites || [];
    if (active !== "all") list = list.filter((f) => f.collection_id === active);
    if (search) list = list.filter((f) => (f.snapshot?.title || "").toLowerCase().includes(search.toLowerCase()));
    if (sort === "title") list = [...list].sort((a, b) => (a.snapshot?.title || "").localeCompare(b.snapshot?.title || ""));
    return list;
  }, [data, active, search, sort]);

  const createCollection = async () => {
    const name = newCol.trim(); if (!name) return;
    setCreating(true);
    try { await api.post("/favorites/collections", { name }); setNewCol(""); toast.success("Collection created 📁"); await load(); }
    catch { toast.error("Couldn't create collection"); }
    finally { setCreating(false); }
  };

  const deleteCollection = async (id) => {
    try { await api.delete(`/favorites/collections/${id}`); if (active === id) setActive("all"); await load(); toast("Collection deleted"); }
    catch (e) { toast.error(e.response?.data?.detail || "Couldn't delete"); }
  };

  const remove = async (fav) => {
    setData((d) => ({ ...d, favorites: d.favorites.filter((f) => f.id !== fav.id) }));
    try { await api.post("/favorites/toggle", { item_type: fav.item_type, item_id: fav.item_id }); reloadFavorites(); }
    catch { load(); }
  };

  const move = async (fav, collectionId) => {
    setData((d) => ({ ...d, favorites: d.favorites.map((f) => f.id === fav.id ? { ...f, collection_id: collectionId || null } : f) }));
    try { await api.patch(`/favorites/${fav.id}`, { collection_id: collectionId || null }); } catch { load(); }
  };

  const openItem = (fav) => {
    if (fav.item_type === "game") navigate(`/app/arcade/${fav.item_id}`);
    else if (fav.item_type === "creator") navigate(`/app/creators/${fav.item_id}`);
  };

  return (
    <div className="p-5 lg:p-8 max-w-7xl mx-auto">
      <div className="flex items-center gap-3 mb-2">
        <Heart className="w-8 h-8 text-rose-500" />
        <h1 className="font-display text-3xl sm:text-4xl font-extrabold">Favourites</h1>
      </div>
      <p className="text-[hsl(var(--muted-foreground))] mb-6">Your saved games, creators and inspiration — organised into collections.</p>

      {!data ? <Spinner /> : (
        <div className="grid lg:grid-cols-[260px_1fr] gap-6">
          {/* Collections sidebar */}
          <aside className="space-y-2" data-testid="fav-collections">
            <button onClick={() => setActive("all")} data-testid="fav-collection-all"
              className={`w-full flex items-center justify-between px-3 py-2.5 rounded-xl text-sm font-semibold ${active === "all" ? "bg-[hsl(var(--primary))] text-white" : "bg-[hsl(var(--card))] border border-[hsl(var(--border))]"}`}>
              <span className="flex items-center gap-2"><Folder className="w-4 h-4" /> All favourites</span>
              <span className="tabular-nums">{favs.length}</span>
            </button>
            {collections.map((c) => (
              <div key={c.id} className={`group flex items-center gap-1 rounded-xl ${active === c.id ? "bg-[hsl(var(--primary))] text-white" : "bg-[hsl(var(--card))] border border-[hsl(var(--border))]"}`}>
                <button onClick={() => setActive(c.id)} data-testid={`fav-collection-${c.id}`} className="flex-1 flex items-center justify-between px-3 py-2.5 text-sm font-semibold">
                  <span className="flex items-center gap-2 truncate">{c.emoji} <span className="truncate">{c.name}</span></span>
                  <span className="tabular-nums">{c.count}</span>
                </button>
                {!c.system && (
                  <button onClick={() => deleteCollection(c.id)} data-testid={`del-collection-${c.id}`} className="px-2 opacity-0 group-hover:opacity-100 transition-opacity">
                    <Trash2 className="w-3.5 h-3.5" />
                  </button>
                )}
              </div>
            ))}
            <div className="flex gap-2 pt-1">
              <Input value={newCol} onChange={(e) => setNewCol(e.target.value)} onKeyDown={(e) => e.key === "Enter" && createCollection()} placeholder="New collection…" className="h-9 text-sm" data-testid="new-collection-input" />
              <Button size="icon" className="h-9 w-9 shrink-0" onClick={createCollection} disabled={creating} data-testid="create-collection-btn"><Plus className="w-4 h-4" /></Button>
            </div>
          </aside>

          {/* Items */}
          <div>
            <div className="flex gap-3 mb-4">
              <div className="relative flex-1 max-w-sm">
                <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-[hsl(var(--muted-foreground))]" />
                <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search favourites…" className="pl-9" data-testid="fav-search" />
              </div>
              <select value={sort} onChange={(e) => setSort(e.target.value)} className="rounded-xl border border-[hsl(var(--border))] bg-[hsl(var(--secondary))] px-3 text-sm font-semibold" data-testid="fav-sort">
                <option value="recent">Recently added</option>
                <option value="title">Title A–Z</option>
              </select>
            </div>

            {visible.length === 0 ? (
              <EmptyState emoji="💛" title="Nothing here yet" text="Tap the heart on any game to save it to your favourites." />
            ) : (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="fav-grid">
                {visible.map((f) => {
                  const s = f.snapshot || {};
                  const w = s.world || {};
                  return (
                    <div key={f.id} data-testid={`fav-item-${f.item_id}`} className="rounded-2xl border border-[hsl(var(--border))] bg-[hsl(var(--card))] overflow-hidden flex flex-col">
                      <button onClick={() => openItem(f)} className="relative h-28 grid place-items-center" style={{ background: `linear-gradient(135deg, ${w.bgColor || "#0b1026"}, ${w.accent || "#1f6feb"})` }}>
                        {s.cover_image ? <img src={`${process.env.REACT_APP_BACKEND_URL}${s.cover_image}`} alt={s.title} className="absolute inset-0 w-full h-full object-cover" /> : <span className="text-4xl">{s.cover_emoji || "🎮"}</span>}
                        <span className="absolute top-2 left-2 px-2 py-0.5 rounded-full text-[10px] font-bold bg-black/50 text-white capitalize">{f.item_type}</span>
                      </button>
                      <div className="p-3 flex-1 flex flex-col">
                        <div className="font-bold truncate">{s.title}</div>
                        <div className="text-xs text-[hsl(var(--muted-foreground))] truncate">{s.subtitle}</div>
                        {f.item_type === "game" && (
                          <div className="flex items-center gap-3 mt-1.5 text-xs text-[hsl(var(--muted-foreground))]">
                            {s.rating > 0 && <span className="flex items-center gap-1"><Star className="w-3 h-3 fill-amber-400 text-amber-400" /> {s.rating}</span>}
                            {s.plays != null && <span className="flex items-center gap-1"><Play className="w-3 h-3" /> {s.plays}</span>}
                          </div>
                        )}
                        <div className="flex items-center gap-2 mt-3">
                          {f.item_type === "game"
                            ? <Button size="sm" className="flex-1 rounded-full" onClick={() => openItem(f)} data-testid={`fav-play-${f.item_id}`}><Play className="w-3.5 h-3.5" fill="white" /> Play</Button>
                            : <Button size="sm" variant="outline" className="flex-1 rounded-full" onClick={() => openItem(f)}>View</Button>}
                          <select value={f.collection_id || ""} onChange={(e) => move(f, e.target.value)} data-testid={`fav-move-${f.item_id}`} className="rounded-full border border-[hsl(var(--border))] bg-[hsl(var(--secondary))] px-2 text-xs max-w-[110px]">
                            <option value="">No folder</option>
                            {collections.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
                          </select>
                          <button onClick={() => remove(f)} data-testid={`fav-remove-${f.item_id}`} className="w-8 h-8 grid place-items-center rounded-full bg-[hsl(var(--secondary))] hover:bg-rose-500/20 text-rose-500"><X className="w-4 h-4" /></button>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
